/*
 * "+" manual orders from clinic forms: the picker's catalogue, the in-memory previews of a picked
 * form, and the orders saved on the visit (Save, the list a reload rebuilds the rows from,
 * Discard, and marking one signed with what signing produced). The catalogue and previews write
 * no data row. Every request is checked before it leaves the browser, and every answer is parsed
 * with the shared contracts and checked to be the order, visit and form asked about.
 */
import {
  type DiscardManualOrderDraftRequest,
  DiscardManualOrderDraftRequestSchema,
  IdSchema,
  type ManualOrderCatalogue,
  ManualOrderCatalogueSchema,
  type ManualOrderDraft,
  type ManualOrderDraftList,
  ManualOrderDraftListSchema,
  type ManualOrderDraftOutcome,
  ManualOrderDraftSchema,
  ManualOrderIdSchema,
  type ManualOrderPreflight,
  type ManualOrderPreflightRequest,
  ManualOrderPreflightRequestSchema,
  ManualOrderPreflightSchema,
  type ManualOrderPreview,
  type ManualOrderPreviewRequestInput,
  ManualOrderPreviewRequestSchema,
  ManualOrderPreviewSchema,
  type MarkManualOrderDraftSignedOutcome,
  type MarkManualOrderDraftSignedRequest,
  MarkManualOrderDraftSignedRequestSchema,
  type PreparedDocumentSelection,
  PreparedDocumentSelectionSchema,
  type SaveManualOrderDraftRequestInput,
  SaveManualOrderDraftRequestSchema,
  sameManualOrderForm,
} from "@emr/contracts";

export interface ManualOrdersClientOptions {
  readonly baseUrl: string;
  /** The workforce site the catalogue and previews are read for. */
  readonly siteId: string;
  readonly getAccessToken: () => Promise<string>;
  readonly fetch?: typeof fetch;
}

/** A preview with its PDF decoded, as the viewer reads it. */
export type ManualOrderPreviewResult = Omit<ManualOrderPreview, "pdfBase64"> & {
  readonly bytes: Uint8Array;
};

/**
 * A refused or failed "+" request, with the server's reason in plain words when it gave one.
 * `detail` says which refusal it is (MANUAL_ORDER_* and the like); `code` is the server's error
 * code (CONTEXT_STALE when the chart moved, POLICY_BLOCKED for a clinic rule, ...).
 */
export class ManualOrderRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail?: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = "ManualOrderRequestError";
  }
}

/**
 * A prepared form set picked up again: the physician's own selection, still current, with every
 * form ready to sign. Anything changed since it was prepared is refused with
 * DOCUMENT_SELECTION_NEEDS_PREPARING.
 */
export type ManualOrderSelection = PreparedDocumentSelection;

export interface ManualOrdersClient {
  /** Every form and order set the "+" picker offers at this site. */
  catalogue(signal?: AbortSignal): Promise<ManualOrderCatalogue>;
  /** Renders a "+" order's form in memory: nothing is saved. */
  preview(
    encounterId: string,
    input: ManualOrderPreviewRequestInput,
    signal?: AbortSignal,
  ): Promise<ManualOrderPreviewResult>;
  /** The visit's saved and signed "+" orders, every physician's, newest first. */
  listDrafts(encounterId: string, signal?: AbortSignal): Promise<ManualOrderDraftList>;
  /** Saves a "+" order in place under its version (0 for its first Save). */
  saveDraft(
    encounterId: string,
    draftId: string,
    input: SaveManualOrderDraftRequestInput,
  ): Promise<ManualOrderDraft>;
  /** Discards the physician's own unsigned "+" order. */
  discardDraft(
    encounterId: string,
    draftId: string,
    input: DiscardManualOrderDraftRequest,
  ): Promise<ManualOrderDraft>;
  /** Marks the physician's own "+" order signed with what signing produced. */
  markSigned(
    encounterId: string,
    draftId: string,
    input: MarkManualOrderDraftSignedRequest,
  ): Promise<ManualOrderDraft>;
  /** A prepared form set of the physician's, still ready to sign, picked up again. */
  getSelection(selectionId: string, signal?: AbortSignal): Promise<ManualOrderSelection>;
  /**
   * Asked right before a prepared lab "+" order is signed: its prepared forms are still exactly
   * the reviewed ones, filled from the chart as it is now. Answers with the patient's possible
   * duplicate orders; refused with MANUAL_ORDER_PREPARE_AGAIN (prepare it again) or
   * MANUAL_ORDER_ALREADY_PLACED (it is placed already).
   */
  preflight(
    encounterId: string,
    manualOrderId: string,
    input: ManualOrderPreflightRequest,
  ): Promise<ManualOrderPreflight>;
}

/** Where the visit's saved "+" orders live: under the encounter, one path per order. */
export function manualOrderDraftsPath(
  encounterId: string,
  draftId?: string,
  action?: "discard" | "signed",
): string {
  const base = `/v2/encounters/${encodeURIComponent(encounterId)}/manual-orders/drafts`;
  if (draftId === undefined) return base;
  const draft = `${base}/${encodeURIComponent(draftId)}`;
  return action === undefined ? draft : `${draft}/${action}`;
}

function apiUrl(baseUrl: string, path: string): string {
  const pageOrigin = globalThis.location?.origin ?? "http://127.0.0.1";
  const base = new URL(baseUrl, pageOrigin);
  const url = new URL(path, base);
  if (url.origin !== base.origin) {
    throw new Error("Clinic form requests must stay on the configured API origin.");
  }
  return url.toString();
}

function decodeBase64(value: string): Uint8Array {
  return Uint8Array.from(atob(value), (character) => character.charCodeAt(0));
}

/**
 * A "+" request the server refused, as a ManualOrderRequestError in plain words. The laboratory
 * preparation of a "+" order (agentClient.prepareLabOrder) reads its refusals the same way.
 */
export async function manualOrderRefusal(
  response: Response,
  fallback: string,
): Promise<ManualOrderRequestError> {
  const body = (await response.json().catch(() => null)) as {
    error?: { message?: unknown; detail?: unknown; code?: unknown };
  } | null;
  const serverMessage =
    typeof body?.error?.message === "string" && body.error.message.trim().length > 0
      ? body.error.message
      : undefined;
  const detail = typeof body?.error?.detail === "string" ? body.error.detail : undefined;
  const code = typeof body?.error?.code === "string" ? body.error.code : undefined;
  if (response.status === 401 || response.status === 403)
    return new ManualOrderRequestError(
      "Your sign-in can't open clinic forms here. Sign in again, then try again.",
      response.status,
      detail,
      code,
    );
  // Conflicts and refusals carry the reason the physician needs (an excluded or changed form, a
  // saved order changed or removed elsewhere), and so does a saved order that is not found: its
  // 404 names why. A 404 without one means the routes are not here.
  if (
    (response.status === 409 ||
      response.status === 422 ||
      (response.status === 404 && detail !== undefined)) &&
    serverMessage !== undefined
  )
    return new ManualOrderRequestError(serverMessage, response.status, detail, code);
  if (response.status === 404 || response.status === 503)
    return new ManualOrderRequestError(
      "Clinic forms aren't available here right now.",
      response.status,
      detail,
      code,
    );
  return new ManualOrderRequestError(fallback, response.status, detail, code);
}

const refusal = manualOrderRefusal;

/**
 * The "+" transport, composed like the fax and incoming-document clients: the workforce token and
 * site go on every request, nothing is cached by the browser, and every response is parsed.
 */
export function createManualOrdersClient(options: ManualOrdersClientOptions): ManualOrdersClient {
  const fetchImplementation = options.fetch ?? fetch;
  const request = async (path: string, init: RequestInit = {}): Promise<Response> => {
    const token = await options.getAccessToken();
    if (token.length === 0) throw new Error("A valid workforce session is required.");
    return fetchImplementation(apiUrl(options.baseUrl, path), {
      ...init,
      headers: {
        authorization: `Bearer ${token}`,
        "x-site-id": options.siteId,
        ...(init.body === undefined ? {} : { "content-type": "application/json" }),
      },
      cache: "no-store",
      credentials: "same-origin",
    });
  };

  return {
    async catalogue(signal) {
      const response = await request(
        `/v2/manual-orders/catalogue?${new URLSearchParams({ siteId: options.siteId }).toString()}`,
        { method: "GET", ...(signal === undefined ? {} : { signal }) },
      );
      if (!response.ok)
        throw await refusal(response, "The clinic's forms could not be loaded. Try again.");
      const parsed = ManualOrderCatalogueSchema.safeParse(await response.json().catch(() => null));
      if (!parsed.success)
        throw new ManualOrderRequestError("The clinic's forms could not be read.", response.status);
      if (parsed.data.siteId !== options.siteId)
        throw new ManualOrderRequestError(
          "The clinic's forms belong to another clinic.",
          response.status,
        );
      return parsed.data;
    },

    async preview(encounterId, input, signal) {
      const body = ManualOrderPreviewRequestSchema.parse(input);
      if (body.siteId !== options.siteId)
        throw new Error("The form preview is outside the signed-in clinic.");
      const response = await request(
        `/v2/encounters/${encodeURIComponent(encounterId)}/manual-orders/previews`,
        {
          method: "POST",
          body: JSON.stringify(body),
          ...(signal === undefined ? {} : { signal }),
        },
      );
      if (!response.ok) throw await refusal(response, "The form could not be shown. Try again.");
      const parsed = ManualOrderPreviewSchema.safeParse(await response.json().catch(() => null));
      if (!parsed.success)
        throw new ManualOrderRequestError("The form preview could not be read.", response.status);
      const { pdfBase64, ...preview } = parsed.data;
      // The preview must be of this order and the form it was picked on, never another.
      if (
        preview.manualOrderId !== body.manualOrderId ||
        !sameManualOrderForm(preview.form, body.form)
      )
        throw new ManualOrderRequestError(
          "The preview that came back is of a different form. Open it again.",
          response.status,
        );
      return { ...preview, bytes: decodeBase64(pdfBase64) };
    },

    async listDrafts(encounterId, signal) {
      const response = await request(
        `${manualOrderDraftsPath(encounterId)}?${new URLSearchParams({ siteId: options.siteId }).toString()}`,
        { method: "GET", ...(signal === undefined ? {} : { signal }) },
      );
      if (!response.ok)
        throw await refusal(response, "The forms saved on this visit could not be loaded.");
      const body = (await response.json().catch(() => null)) as unknown;
      const parsed = ManualOrderDraftListSchema.safeParse(
        Array.isArray(body) ? body : (body as { drafts?: unknown } | null)?.drafts,
      );
      if (!parsed.success)
        throw new ManualOrderRequestError(
          "The forms saved on this visit could not be read.",
          response.status,
        );
      // Every row must be of this visit: one that is not means the answer is not to be trusted.
      if (parsed.data.some((draft) => draft.encounterId !== encounterId))
        throw new ManualOrderRequestError(
          "The saved forms that came back belong to another visit.",
          response.status,
        );
      return parsed.data;
    },

    async saveDraft(encounterId, draftId, input) {
      const id = ManualOrderIdSchema.parse(draftId);
      const body = SaveManualOrderDraftRequestSchema.parse(input);
      if (body.siteId !== options.siteId)
        throw new Error("The form is outside the signed-in clinic.");
      const response = await request(manualOrderDraftsPath(encounterId, id), {
        method: "PUT",
        body: JSON.stringify(body),
      });
      if (!response.ok) throw await refusal(response, "The form could not be saved. Try again.");
      const draft = await parsedDraft(response, "The saved form could not be read.");
      // The answer must be this order as it was just saved: the same visit, kind and form, ours.
      if (
        draft.id !== id ||
        draft.encounterId !== encounterId ||
        draft.patientId !== body.patientId ||
        draft.kind !== body.kind ||
        !sameManualOrderForm(draft.form, body.form) ||
        !draft.mine ||
        draft.status !== "draft" ||
        draft.version <= body.expectedVersion
      )
        throw new ManualOrderRequestError(
          "The saved form that came back is not the one sent. Open the list again.",
          response.status,
        );
      return draft;
    },

    async discardDraft(encounterId, draftId, input) {
      const id = ManualOrderIdSchema.parse(draftId);
      const body = DiscardManualOrderDraftRequestSchema.parse(input);
      if (body.siteId !== options.siteId)
        throw new Error("The form is outside the signed-in clinic.");
      const response = await request(manualOrderDraftsPath(encounterId, id, "discard"), {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!response.ok)
        throw await refusal(response, "The saved form could not be discarded. Try again.");
      const draft = await parsedDraft(response, "The discarded form could not be read.");
      if (draft.id !== id || draft.encounterId !== encounterId || draft.status !== "discarded")
        throw new ManualOrderRequestError(
          "The form that came back was not discarded. Open the list again.",
          response.status,
        );
      return draft;
    },

    async markSigned(encounterId, draftId, input) {
      const id = ManualOrderIdSchema.parse(draftId);
      const body = MarkManualOrderDraftSignedRequestSchema.parse(input);
      if (body.siteId !== options.siteId)
        throw new Error("The form is outside the signed-in clinic.");
      const response = await request(manualOrderDraftsPath(encounterId, id, "signed"), {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!response.ok)
        throw await refusal(response, "The signed form could not be recorded. Try again.");
      const draft = await parsedDraft(response, "The signed form could not be read.");
      if (
        draft.id !== id ||
        draft.encounterId !== encounterId ||
        draft.status !== "signed" ||
        draft.outcome === undefined ||
        !sameManualOrderOutcome(draft.outcome, body.outcome)
      )
        throw new ManualOrderRequestError(
          "The form that came back is not the one signed. Open the list again.",
          response.status,
        );
      return draft;
    },

    async getSelection(selectionId, signal) {
      const id = IdSchema.parse(selectionId);
      const response = await request(
        `/v2/document-selections/${encodeURIComponent(id)}?${new URLSearchParams({ siteId: options.siteId }).toString()}`,
        { method: "GET", ...(signal === undefined ? {} : { signal }) },
      );
      if (!response.ok)
        throw await refusal(response, "The prepared form could not be loaded. Try again.");
      const parsed = PreparedDocumentSelectionSchema.safeParse(
        await response.json().catch(() => null),
      );
      if (!parsed.success)
        throw new ManualOrderRequestError("The prepared form could not be read.", response.status);
      const { selection, artifacts } = parsed.data;
      if (
        selection.id !== id ||
        selection.siteId !== options.siteId ||
        artifacts.some((artifact) => artifact.selectionId !== id)
      )
        throw new ManualOrderRequestError(
          "The form that came back belongs to another signing.",
          response.status,
        );
      return { selection, artifacts };
    },

    async preflight(encounterId, manualOrderId, input) {
      const id = ManualOrderIdSchema.parse(manualOrderId);
      const body = ManualOrderPreflightRequestSchema.parse(input);
      if (body.siteId !== options.siteId)
        throw new Error("The order is outside the signed-in clinic.");
      const response = await request(manualOrderPreflightPath(encounterId, id), {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!response.ok)
        throw await refusal(response, "The prepared order could not be checked. Try again.");
      const parsed = ManualOrderPreflightSchema.safeParse(await response.json().catch(() => null));
      if (!parsed.success)
        throw new ManualOrderRequestError(
          "The check of the prepared order could not be read.",
          response.status,
        );
      return parsed.data;
    },
  };
}

/** Where a prepared lab "+" order is checked right before it is signed. */
export function manualOrderPreflightPath(encounterId: string, manualOrderId: string): string {
  return `/v2/encounters/${encodeURIComponent(encounterId)}/manual-orders/${encodeURIComponent(manualOrderId)}/preflight`;
}

/**
 * True when a recorded outcome is the one marked: the same signed document, or the same placed lab
 * order (its bundle and exact order action). The receipt the database found is not compared.
 */
export function sameManualOrderOutcome(
  recorded: ManualOrderDraftOutcome,
  marked: MarkManualOrderDraftSignedOutcome,
): boolean {
  if (recorded.kind === "document")
    return marked.kind === "document" && recorded.selectionId === marked.selectionId;
  return (
    marked.kind === recorded.kind &&
    recorded.bundleId === marked.bundleId &&
    recorded.actionId === marked.actionId &&
    recorded.actionVersion === marked.actionVersion
  );
}

async function parsedDraft(response: Response, unreadable: string): Promise<ManualOrderDraft> {
  const parsed = ManualOrderDraftSchema.safeParse(await response.json().catch(() => null));
  if (!parsed.success) throw new ManualOrderRequestError(unreadable, response.status);
  return parsed.data;
}
